// Remove ws import and use native WebSocket
import { api } from '../utils/api';

interface CallState {
  isMuted: boolean;
  isSpeakerOn: boolean;
  isVideoEnabled: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  peerConnection: RTCPeerConnection | null;
  isInCall: boolean;
  pendingOffer?: RTCSessionDescriptionInit;
  pendingCallerId?: string;
  remoteUserId?: string;
  callStartTime?: number;
  callQuality?: {
    bitrate: number;
    packetLoss: number;
    roundTripTime: number;
  };
}

interface WebSocketMessage {
  type: string;
  payload: any;
}

interface Contact {
  id: string;
  name: string;
  relationship: string;
  phone?: string;
  email?: string;
}

class CallService {
  private static instance: CallService;
  private socket!: WebSocket;
  private userId: string = '';
  private userName: string = '';
  private isCleaningUp: boolean = false;
  private callState: CallState = {
    isMuted: false,
    isSpeakerOn: true,
    isVideoEnabled: true,
    localStream: null,
    remoteStream: null,
    peerConnection: null,
    isInCall: false,
    callQuality: {
      bitrate: 0,
      packetLoss: 0,
      roundTripTime: 0
    }
  };
  private callStateChangeListener: ((state: CallState) => void) | null = null;
  private callStatus: 'idle' | 'calling' | 'ringing' | 'in-call' | 'declined' | 'cancelled' | 'missed' = 'idle';
  private callStatusListener: ((status: string, payload?: any) => void) | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 3;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pendingIceCandidates: RTCIceCandidate[] = [];
  private qualityMonitorInterval: NodeJS.Timeout | null = null;
  private readonly WS_URL: string;
  private readonly TURN_SERVERS: RTCIceServer[];
  private activeCall: any = null;
  private messageQueue: Array<{ type: string; payload: any }> = [];
  private isRegistering: boolean = false;
  private registrationPromise: Promise<void>;
  private registrationResolve: (() => void) | null = null;
  public isRegistered: boolean = false;
  public connectionStatus: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  private connectionStatusListener: ((status: string) => void) | null = null;

  private constructor() {
    // Load configuration from environment variables
    this.WS_URL = process.env.REACT_APP_WS_URL || 'wss://lavernaibackend.onrender.com';
    console.log('[CallService] Initializing with WebSocket URL:', this.WS_URL);
    
    // Registration promise setup
    this.registrationPromise = new Promise((resolve) => {
      this.registrationResolve = resolve;
    });
    this.isRegistered = false;
    
    // Initialize WebSocket with better error handling
    try {
      this.socket = new WebSocket(this.WS_URL);
      console.log('[CallService] WebSocket instance created');
    } catch (error) {
      console.error('[CallService] Failed to create WebSocket:', error);
      throw error;
    }
    
    // Configure ICE servers with fallbacks and production-ready setup
    this.TURN_SERVERS = [
      // Primary STUN servers
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      // Backup STUN servers
      { urls: 'stun:stun2.l.google.com:19302' },
      { urls: 'stun:stun3.l.google.com:19302' },
      { urls: 'stun:stun4.l.google.com:19302' }
    ];

    // Log ICE server configuration (without credentials)
    console.log('[CallService] ICE Server Configuration:', 
      this.TURN_SERVERS.map(server => ({
        urls: server.urls,
        hasCredentials: !!(server.username && server.credential)
      }))
    );

    this.setupSocketListeners();
  }

  private setupSocketListeners() {
    this.socket.onopen = () => {
      this.connectionStatus = 'connected';
      if (this.connectionStatusListener) this.connectionStatusListener('connected');
      console.log('[CallService] WebSocket connection established');
      this.reconnectAttempts = 0;
      
      // If we have a userId, register immediately
      if (this.userId) {
        this.isRegistering = true;
        this.socket.send(JSON.stringify({ type: 'register', userId: this.userId }));
        console.log('[CallService] Sent register message (onopen):', this.userId);
      }
      
      // Flush queued messages
      this.flushMessageQueue();
    };

    this.socket.onerror = (error) => {
      this.connectionStatus = 'disconnected';
      if (this.connectionStatusListener) this.connectionStatusListener('disconnected');
      console.error('[CallService] WebSocket error:', error);
      
      // Reset registration state on error
      this.isRegistered = false;
      this.isRegistering = false;
    };

    this.socket.onclose = (event) => {
      this.connectionStatus = 'disconnected';
      if (this.connectionStatusListener) this.connectionStatusListener('disconnected');
      console.log('[CallService] WebSocket connection closed:', event.code, event.reason);
      
      // Reset registration state on close
      this.isRegistered = false;
      this.isRegistering = false;
      
      // Attempt reconnection
      this.attemptReconnect();
    };

    this.socket.onmessage = async (event: MessageEvent) => {
      console.log('[CallService] WebSocket message received (RAW):', event.data);
      console.log('[CallService] WebSocket message received for userId', this.userId, ':', event.data);
      
      try {
        const data = JSON.parse(event.data) as WebSocketMessage;
        
        // Handle registration acknowledgment
        if (data.type === 'register:ack') {
          console.log('[CallService] Registration acknowledged');
          this.isRegistering = false;
          this.isRegistered = true;
          if (this.registrationResolve) {
            this.registrationResolve();
            this.registrationResolve = null;
          }
          this.flushMessageQueue();
          return;
        }
        
        // Handle incoming call messages
        switch (data.type) {
          case 'call:ringing':
            console.log('[CallService] Call ringing received:', data.payload);
            if (this.callStatus === 'idle' || this.callStatus === 'calling') {
              this.callStatus = 'ringing';
              this.callState.remoteUserId = data.payload.callerId;
              this.callState.callStartTime = Date.now();
              
              if (this.callStatusListener) {
                this.callStatusListener('ringing', data.payload);
              }
            }
            break;
            
          case 'call:offer':
            console.log('[CallService] Call offer received:', data.payload);
            if (this.callStatus === 'ringing' && this.callState.remoteUserId === data.payload.callerId) {
              // Store the offer for when the user answers
              this.callState.pendingOffer = data.payload.offer;
              console.log('[CallService] Stored pending offer for incoming call');
            } else if (this.callStatus === 'idle') {
              // If we receive an offer without ringing, set up the call state
              this.callStatus = 'ringing';
              this.callState.remoteUserId = data.payload.callerId;
              this.callState.callStartTime = Date.now();
              this.callState.pendingOffer = data.payload.offer;
              
              if (this.callStatusListener) {
                this.callStatusListener('ringing', {
                  callerId: data.payload.callerId,
                  callerName: 'Incoming call'
                });
              }
            }
            break;
            
          case 'call:declined':
            console.log('[CallService] Call declined received:', data.payload);
            this.callStatus = 'declined';
            if (this.callStatusListener) {
              this.callStatusListener('declined', data.payload);
            }
            setTimeout(() => this.cleanupCall(), 2000);
            break;
            
          case 'call:cancelled':
            console.log('[CallService] Call cancelled received:', data.payload);
            this.callStatus = 'cancelled';
            if (this.callStatusListener) {
              this.callStatusListener('cancelled', data.payload);
            }
            setTimeout(() => this.cleanupCall(), 2000);
            break;
          case 'call:ice-candidate':
            console.log('[CallService] Received call:ice-candidate', data.payload);
            try {
              await this.addIceCandidate(new RTCIceCandidate(data.payload.candidate));
            } catch (error) {
              console.error('[CallService] Error handling ICE candidate:', error);
            }
            break;
          case 'call:answer':
            console.log('[CallService] Received call:answer', data.payload);
            try {
              if (this.callState.peerConnection) {
                await this.callState.peerConnection.setRemoteDescription(new RTCSessionDescription(data.payload.answer));
                await this.applyPendingIceCandidates();
                this.callState.isInCall = true;
                this.callStatus = 'in-call';
                this.notifyStateChange();
                if (this.callStatusListener) this.callStatusListener('in-call');
              } else {
                console.error('[CallService] No peer connection available when receiving answer');
                this.endCall();
              }
            } catch (error) {
              console.error('[CallService] Error handling call answer:', error);
              this.endCall();
            }
            break;
          case 'call:ended':
            console.log('[CallService] Received call:ended', data.payload);
            setTimeout(() => this.cleanupCall(), 2000);
            break;
        }
      } catch (error) {
        console.error('[CallService] Error processing WebSocket message:', error);
      }
    };
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[CallService] Max reconnect attempts reached. Giving up.');
      return;
    }
    this.reconnectAttempts++;
    setTimeout(() => {
      console.log('[CallService] Attempting to reconnect WebSocket...');
      this.socket = new WebSocket(this.WS_URL);
      this.setupSocketListeners();
      if (this.userId) {
        this.isRegistering = true;
        this.socket.onopen = () => {
          this.socket.send(JSON.stringify({ type: 'register', userId: this.userId }));
          console.log('[CallService] Sent register message (reconnect):', this.userId);
        };
      }
    }, 1000 * this.reconnectAttempts);
  }

  private async createPeerConnection(): Promise<RTCPeerConnection> {
    try {
      const configuration: RTCConfiguration = {
        iceServers: this.TURN_SERVERS,
        iceCandidatePoolSize: 10,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceTransportPolicy: 'all'
      };

      const peerConnection = new RTCPeerConnection(configuration);

      // Set up event handlers
      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          console.log('[CallService] New ICE candidate:', event.candidate);
          this.sendOrQueue('call:ice-candidate', {
            candidate: event.candidate,
            recipientId: this.callState.remoteUserId
          });
        }
      };

      peerConnection.oniceconnectionstatechange = () => {
        console.log('[CallService] ICE connection state:', peerConnection.iceConnectionState);
        
        switch (peerConnection.iceConnectionState) {
          case 'disconnected':
          case 'failed':
            this.handleConnectionError({
              name: 'NetworkError',
              message: `ICE connection ${peerConnection.iceConnectionState}`
            });
            break;
          case 'checking':
            console.log('[CallService] Checking ICE connection...');
            break;
          case 'connected':
            console.log('[CallService] ICE connection established');
            break;
        }
      };

      peerConnection.onconnectionstatechange = () => {
        console.log('[CallService] Connection state:', peerConnection.connectionState);
        
        switch (peerConnection.connectionState) {
          case 'failed':
            this.handleConnectionError({
              name: 'NetworkError',
              message: 'Peer connection failed'
            });
            break;
          case 'disconnected':
            this.handleConnectionError({
              name: 'NetworkError',
              message: 'Peer connection disconnected'
            });
            break;
        }
      };

      peerConnection.ontrack = (event) => {
        console.log('[CallService] Received remote track:', event.track.kind);
        if (event.streams && event.streams[0]) {
          this.callState.remoteStream = event.streams[0];
          this.notifyStateChange();
        }
      };

      // Add local stream tracks
      if (this.callState.localStream) {
        this.callState.localStream.getTracks().forEach(track => {
          peerConnection.addTrack(track, this.callState.localStream!);
        });
      }

      // Start monitoring connection quality
      this.monitorConnectionQuality(peerConnection);

      return peerConnection;
    } catch (error) {
      this.handleConnectionError(error);
      throw error;
    }
  }

  private async addIceCandidate(candidate: RTCIceCandidate) {
    if (this.callState.peerConnection) {
      try {
        if (this.callState.peerConnection.remoteDescription) {
          await this.callState.peerConnection.addIceCandidate(candidate);
        } else {
          console.log('[CallService] Queueing ICE candidate for later');
          this.pendingIceCandidates.push(candidate);
        }
      } catch (error) {
        console.error('[CallService] Error adding ICE candidate:', error);
      }
    }
  }

  private async applyPendingIceCandidates() {
    if (this.callState.peerConnection && this.callState.peerConnection.remoteDescription) {
      console.log('[CallService] Applying pending ICE candidates:', this.pendingIceCandidates.length);
      for (const candidate of this.pendingIceCandidates) {
        try {
          await this.callState.peerConnection.addIceCandidate(candidate);
        } catch (error) {
          console.error('[CallService] Error applying pending ICE candidate:', error);
        }
      }
      this.pendingIceCandidates = [];
    }
  }

  public setUserId(id: string) {
    this.userId = id;
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.isRegistering = true;
      this.socket.send(JSON.stringify({ type: 'register', userId: this.userId }));
      console.log('[CallService] Sent register message (setUserId):', this.userId);
    }
  }

  public setUserName(name: string) {
    this.userName = name;
  }

  public getUserId(): string {
    return this.userId;
  }

  public getUserName(): string {
    return this.userName;
  }

  public setLocalStream(stream: MediaStream) {
    console.log('[CallService] Setting local stream:', stream.getTracks().map(t => t.kind));
    this.callState.localStream = stream;
    this.notifyStateChange();
  }

  public getCallState(): CallState {
    return { ...this.callState };
  }

  public setCallStateChangeListener(listener: (state: CallState) => void) {
    this.callStateChangeListener = listener;
    // Immediately notify the listener of current state
    if (listener) {
      listener(this.getCallState());
    }
  }

  private notifyStateChange() {
    if (this.callStateChangeListener) {
      this.callStateChangeListener({ ...this.callState });
    }
  }

  public getPeerConnection(): RTCPeerConnection | null {
    return this.callState.peerConnection;
  }

  public setCallStatusListener(listener: (status: string, payload?: any) => void) {
    this.callStatusListener = listener;
  }

  public getCallStatus() {
    return this.callStatus;
  }

  public async startCall(recipientId: string, recipientName: string, recipientEmail: string) {
    // Wait for registration to complete before proceeding
    if (!this.isRegistered) {
      console.log('[CallService] Waiting for registration to complete...');
      try {
        await this.waitForRegistration();
        console.log('[CallService] Registration completed, proceeding with call');
      } catch (error) {
        console.error('[CallService] Failed to wait for registration:', error);
        throw new Error('Call service not ready. Please try again.');
      }
    }

    console.log('[CallService] Starting call to:', recipientId);
    try {
      if (!this.callState.localStream) {
        console.log('[CallService] No local stream available, attempting to initialize...');
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });
          this.setLocalStream(stream);
        } catch (error) {
          console.error('[CallService] Failed to initialize local stream:', error);
          throw new Error('Failed to access camera and microphone');
        }
      }

      // Create and configure peer connection
      const peerConnection = await this.createPeerConnection();
      this.callState.remoteUserId = recipientId;
      this.callState.callStartTime = Date.now();

      // Create and send offer
      const offer = await peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      await peerConnection.setLocalDescription(offer);

      // Send the offer through WebSocket
      this.sendOrQueue('call:offer', {
        offer: offer,
        recipientId: recipientId,
        callerId: this.userId,
        callerName: this.userName
      });

      // Update call status
      this.callStatus = 'calling';
      if (this.callStatusListener) {
        this.callStatusListener('calling', { recipientId, recipientName });
      }

      return true;
    } catch (error) {
      console.error('[CallService] Error starting call:', error);
      this.cleanupCall();
      throw error;
    }
  }

  public async answerCall(callerId: string) {
    try {
      console.log('[CallService] Answering call from:', callerId);
      
      if (!this.callState.pendingOffer) {
        throw new Error('No pending offer available');
      }

      // Create and configure peer connection
      const peerConnection = await this.createPeerConnection();

      // Set remote description from the pending offer
      await peerConnection.setRemoteDescription(new RTCSessionDescription(this.callState.pendingOffer));

      // Create and send answer
      console.log('[CallService] Creating answer...');
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);

      console.log('[CallService] Sending answer to:', callerId);
      this.sendOrQueue('call:answer', {
        answer: peerConnection.localDescription,
        recipientId: callerId
      });

      this.callState.isInCall = true;
      this.callStatus = 'in-call';
      this.callState.remoteUserId = callerId;
      this.notifyStateChange();
      if (this.callStatusListener) {
        this.callStatusListener('in-call');
      }

      return true;
    } catch (error) {
      console.error('[CallService] Error answering call:', error);
      this.cleanupCall();
      throw error;
    }
  }

  public declineCall(callerId: string, calleeName: string) {
    this.sendOrQueue('call:declined', { callerId, calleeId: this.userId, calleeName });
    this.callStatus = 'declined';
    if (this.callStatusListener) this.callStatusListener('declined');
    this.cleanupCall();
  }

  public cancelCall(recipientId: string) {
    this.sendOrQueue('call:cancelled', { recipientId, callerId: this.userId });
    this.callStatus = 'cancelled';
    if (this.callStatusListener) this.callStatusListener('cancelled');
    this.cleanupCall();
  }

  public async endCall(): Promise<void> {
    if (this.activeCall) {
      try {
        await api.post(`/calls/${this.activeCall.id}/end`);
        this.activeCall = null;

        // Dispatch event for call ended
        const event = new CustomEvent('call-ended');
        window.dispatchEvent(event);
      } catch (error) {
        console.error('[Call] Error ending call:', error);
        throw error;
      }
    }

    // Clean up WebRTC resources
    this.cleanupCall();
  }

  public cleanupCall() {
    if (this.isCleaningUp) return;
    this.isCleaningUp = true;

    console.log('[CallService] Cleaning up call...');

    // Stop quality monitoring
    if (this.qualityMonitorInterval) {
      clearInterval(this.qualityMonitorInterval);
      this.qualityMonitorInterval = null;
    }

    // Stop all tracks in local stream
    if (this.callState.localStream) {
      this.callState.localStream.getTracks().forEach(track => {
        console.log(`[CallService] Stopping track: ${track.kind}`);
        track.stop();
      });
    }

    // Close peer connection
    if (this.callState.peerConnection) {
      this.callState.peerConnection.close();
    }

    // Reset call state
    this.callState = {
      ...this.callState,
      localStream: null,
      remoteStream: null,
      peerConnection: null,
      isInCall: false,
      pendingOffer: undefined,
      pendingCallerId: undefined,
      remoteUserId: undefined,
      callStartTime: undefined,
      callQuality: {
        bitrate: 0,
        packetLoss: 0,
        roundTripTime: 0
      }
    };

    this.callStatus = 'idle';
    this.isCleaningUp = false;
    this.notifyStateChange();
  }

  private async monitorConnectionQuality(peerConnection: RTCPeerConnection) {
    if (this.qualityMonitorInterval) {
      clearInterval(this.qualityMonitorInterval);
    }

    this.qualityMonitorInterval = setInterval(async () => {
      try {
        const stats = await peerConnection.getStats();
        let totalBitrate = 0;
        let totalPacketLoss = 0;
        let totalRoundTripTime = 0;
        let sampleCount = 0;

        stats.forEach(report => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            const bytesReceived = report.bytesReceived;
            const timestamp = report.timestamp;
            
            // Calculate bitrate
            if (this.callState.callStartTime) {
              const duration = (timestamp - this.callState.callStartTime) / 1000; // in seconds
              const bitrate = (bytesReceived * 8) / duration; // bits per second
              totalBitrate += bitrate;
            }

            // Calculate packet loss
            if (report.packetsLost !== undefined && report.packetsReceived !== undefined) {
              const packetsLost = report.packetsLost;
              const packetsReceived = report.packetsReceived;
              const totalPackets = packetsLost + packetsReceived;
              if (totalPackets > 0) {
                const packetLoss = (packetsLost / totalPackets) * 100;
                totalPacketLoss += packetLoss;
              }
            }
          }

          // Calculate round trip time
          if (report.type === 'candidate-pair' && report.currentRoundTripTime) {
            totalRoundTripTime += report.currentRoundTripTime;
            sampleCount++;
          }
        });

        // Update call quality metrics
        this.callState.callQuality = {
          bitrate: totalBitrate,
          packetLoss: totalPacketLoss,
          roundTripTime: sampleCount > 0 ? totalRoundTripTime / sampleCount : 0
        };

        // Log quality metrics for monitoring
        console.log('[CallService] Connection Quality:', {
          bitrate: `${(this.callState.callQuality.bitrate / 1000).toFixed(2)} kbps`,
          packetLoss: `${this.callState.callQuality.packetLoss.toFixed(2)}%`,
          roundTripTime: `${(this.callState.callQuality.roundTripTime * 1000).toFixed(2)} ms`
        });

        // Notify state change to update UI
        this.notifyStateChange();

        // Check for poor connection quality
        if (this.callState.callQuality.packetLoss > 10 || this.callState.callQuality.roundTripTime > 0.5) {
          console.warn('[CallService] Poor connection quality detected');
          if (this.callStatusListener) {
            this.callStatusListener('quality-warning', this.callState.callQuality);
          }
        }
      } catch (error) {
        console.error('[CallService] Error monitoring connection quality:', error);
      }
    }, 2000); // Monitor every 2 seconds
  }

  private handleConnectionError(error: any) {
    console.error('[CallService] Connection error:', error);
    
    // Determine error type and severity
    const errorType = this.determineErrorType(error);
    const errorSeverity = this.determineErrorSeverity(error);
    
    // Log error for monitoring
    console.error('[CallService] Error details:', {
      type: errorType,
      severity: errorSeverity,
      message: error.message || 'Unknown error',
      timestamp: new Date().toISOString()
    });

    // Handle based on severity
    switch (errorSeverity) {
      case 'critical':
        this.endCall();
        if (this.callStatusListener) {
          this.callStatusListener('error', {
            type: errorType,
            message: 'Connection lost. Please try again.'
          });
        }
        break;
      case 'warning':
        if (this.callStatusListener) {
          this.callStatusListener('warning', {
            type: errorType,
            message: 'Connection issues detected. Call quality may be affected.'
          });
        }
        break;
      default:
        console.warn('[CallService] Non-critical error:', error);
    }
  }

  private determineErrorType(error: any): string {
    if (error.name === 'NotAllowedError') return 'permission-denied';
    if (error.name === 'NotFoundError') return 'device-not-found';
    if (error.name === 'NotReadableError') return 'device-in-use';
    if (error.name === 'OverconstrainedError') return 'constraints-not-satisfied';
    if (error.name === 'TypeError') return 'invalid-constraints';
    if (error.name === 'AbortError') return 'aborted';
    if (error.name === 'NetworkError') return 'network-error';
    return 'unknown';
  }

  private determineErrorSeverity(error: any): 'critical' | 'warning' | 'info' {
    const criticalErrors = ['network-error', 'permission-denied'];
    const warningErrors = ['device-in-use', 'constraints-not-satisfied'];
    
    const errorType = this.determineErrorType(error);
    
    if (criticalErrors.includes(errorType)) return 'critical';
    if (warningErrors.includes(errorType)) return 'warning';
    return 'info';
  }

  public toggleMute() {
    if (this.callState.localStream) {
      const audioTracks = this.callState.localStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      this.callState.isMuted = !this.callState.isMuted;
      this.notifyStateChange();
    }
  }

  public toggleVideo() {
    if (this.callState.localStream) {
      const videoTracks = this.callState.localStream.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      this.callState.isVideoEnabled = !this.callState.isVideoEnabled;
      this.notifyStateChange();
    }
  }

  public toggleSpeaker() {
    this.callState.isSpeakerOn = !this.callState.isSpeakerOn;
    this.notifyStateChange();
  }

  public toggleHold() {
    // Implement hold functionality if needed
    // For now, just toggle mute as a basic hold implementation
    if (this.callState.localStream) {
      const audioTracks = this.callState.localStream.getAudioTracks();
      const videoTracks = this.callState.localStream.getVideoTracks();
      
      const isCurrentlyOnHold = !audioTracks[0]?.enabled;
      
      audioTracks.forEach(track => {
        track.enabled = isCurrentlyOnHold;
      });
      videoTracks.forEach(track => {
        track.enabled = isCurrentlyOnHold;
      });
      
      this.notifyStateChange();
    }
  }

  public static getInstance(): CallService {
    if (!CallService.instance) {
      CallService.instance = new CallService();
    }
    return CallService.instance;
  }

  public async initiateEmergencyCalls(contacts: Contact[]): Promise<void> {
    try {
      // Filter contacts with phone numbers
      const contactsWithPhone = contacts.filter(contact => contact.phone);

      // Initiate calls to all contacts
      for (const contact of contactsWithPhone) {
        if (contact.phone) {
          await this.initiateCall(contact);
        }
      }
    } catch (error) {
      console.error('[Call] Error initiating emergency calls:', error);
      throw error;
    }
  }

  private async initiateCall(contact: Contact): Promise<void> {
    try {
      // Here you would integrate with your actual calling service
      // For example, using Twilio or a similar service
      const response = await fetch('/api/calls/initiate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: contact.phone,
          contactId: contact.id,
          isEmergency: true
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to initiate call: ${response.statusText}`);
      }

      const callData = await response.json();
      this.activeCall = callData;

      // Start WebRTC call
      await this.startCall(contact.id, contact.name, contact.email || '');

      // Dispatch event for call initiated
      const event = new CustomEvent('call-initiated', {
        detail: { contact, callData }
      });
      window.dispatchEvent(event);
    } catch (error) {
      console.error(`[Call] Error calling ${contact.name}:`, error);
      throw error;
    }
  }

  public isCallActive(): boolean {
    return this.activeCall !== null;
  }

  public getActiveCall(): any {
    return this.activeCall;
  }

  private sendOrQueue(type: string, payload: any) {
    if (this.socket.readyState === WebSocket.OPEN && !this.isRegistering) {
      try {
        this.socket.send(JSON.stringify({ type, payload }));
        console.log('[CallService] Sent message:', type, payload);
      } catch (err) {
        console.error('[CallService] Error sending message, queueing:', type, payload, err);
        this.messageQueue.push({ type, payload });
      }
    } else {
      console.warn('[CallService] Socket not open, queueing message:', type, payload);
      this.messageQueue.push({ type, payload });
    }
  }

  private flushMessageQueue() {
    if (this.socket.readyState === WebSocket.OPEN && !this.isRegistering) {
      while (this.messageQueue.length > 0) {
        const msg = this.messageQueue.shift();
        if (msg) {
          try {
            this.socket.send(JSON.stringify({ type: msg.type, payload: msg.payload }));
            console.log('[CallService] Flushed queued message:', msg.type, msg.payload);
          } catch (err) {
            console.error('[CallService] Error flushing message, re-queueing:', msg, err);
            this.messageQueue.unshift(msg);
            break;
          }
        }
      }
    }
  }

  public async waitForRegistration() {
    if (this.isRegistered) return;
    await this.registrationPromise;
  }

  public setConnectionStatusListener(listener: (status: string) => void) {
    this.connectionStatusListener = listener;
  }

  public getConnectionStatus(): string {
    return this.connectionStatus;
  }

  public getWebSocketState(): number {
    return this.socket ? this.socket.readyState : -1;
  }

  public getWebSocketStateString(): string {
    const state = this.getWebSocketState();
    switch (state) {
      case WebSocket.CONNECTING: return 'CONNECTING';
      case WebSocket.OPEN: return 'OPEN';
      case WebSocket.CLOSING: return 'CLOSING';
      case WebSocket.CLOSED: return 'CLOSED';
      default: return 'UNKNOWN';
    }
  }

  public debugConnection(): void {
    console.log('[CallService] Connection Debug Info:');
    console.log('- Connection Status:', this.connectionStatus);
    console.log('- WebSocket State:', this.getWebSocketStateString());
    console.log('- Is Registered:', this.isRegistered);
    console.log('- Is Registering:', this.isRegistering);
    console.log('- User ID:', this.userId);
    console.log('- WebSocket URL:', this.WS_URL);
    console.log('- Message Queue Length:', this.messageQueue.length);
  }
}

export default CallService;